/**
 * Moveability Scoring Module - Phase 1.1
 * Last-Minute Insertion Implementation
 *
 * Determines which aircraft are "cheap" to move when making space
 * for last-minute insertions.
 *
 * Lower score = easier to move
 *
 * Scoring factors:
 * 1. Priority (higher priority = harder to move)
 * 2. Locked status (locked = essentially unmoveable)
 * 3. Fairness (exponential penalty for recent moves)
 * 4. Dwell length (longer dwell = harder to re-place)
 * 5. Alternative spots (fewer alternatives = harder to move)
 */

import { ParkingRequest, Spot } from "./types";

/**
 * Configuration options for moveability scoring
 *
 * Default weight rationale:
 * - priorityWeight=100: Makes priority the dominant factor (priority 1→9 = 800 point swing)
 * - dwellWeight=1.0: Baseline factor (24-hour dwell = 96 points)
 * - fairnessWeight=50: Significant fairness enforcement (2 moves = 150 point penalty)
 * - alternativesWeight=20: Moderate influence (no alternatives = 200 point penalty)
 *
 * Relative importance: Priority > Fairness > Alternatives > Dwell
 */
export interface MoveabilityOptions {
  /**
   * Weight multiplier for priority factor (default: 100)
   * Priority 1 → 100 points, Priority 9 → 900 points
   * Makes priority the dominant factor in moveability
   */
  priorityWeight?: number;

  /**
   * Weight multiplier for dwell length factor (default: 1.0)
   * Each 15-min bucket adds 1 point
   * 24-hour dwell = 96 points
   */
  dwellWeight?: number;

  /**
   * Weight multiplier for fairness factor (default: 50)
   * Exponential penalty: 0 moves → 0, 1 move → 50, 2 moves → 150, 3 moves → 350
   * Prevents repeatedly moving the same aircraft
   */
  fairnessWeight?: number;

  /**
   * Weight multiplier for alternatives factor (default: 20)
   * Based on flexibility ratio: (1 - alternatives/totalSpots) × 20 × 10
   * 0% alternatives = 200 points, 50% = 100 points, 100% = 0 points
   */
  alternativesWeight?: number;

  /** Current time bucket for lock validation (default: 0) */
  currentBucket?: number;
}

/**
 * Compute moveability score for an aircraft
 *
 * Lower score = easier to move
 *
 * @param aircraft Aircraft to evaluate
 * @param spots All available spots on ramp
 * @param options Scoring configuration
 * @returns Moveability score (lower = easier to move)
 */
export function computeMoveability(
  aircraft: ParkingRequest,
  spots: Spot[],
  options: MoveabilityOptions = {}
): number {
  const {
    priorityWeight = 100,
    dwellWeight = 1.0,
    fairnessWeight = 50,
    alternativesWeight = 20,
    currentBucket = 0,
  } = options;

  let score = 0;

  // 1. Priority factor (higher priority = harder to move)
  const priority = aircraft.priority ?? 5; // Default priority is 5 (use ?? to handle 0)
  score += priority * priorityWeight;

  // 2. Locked status (essentially unmoveable)
  if (
    aircraft.lockedUntilBucket &&
    currentBucket < aircraft.lockedUntilBucket
  ) {
    score += 10000; // Make locked aircraft essentially unmoveable
  }

  // 3. Fairness - exponential penalty for recent moves
  // Formula: 2^n - 1, where n = number of moves
  // Results: 0 moves → 0, 1 move → 1, 2 moves → 3, 3 moves → 7, etc.
  const moveHistory = aircraft.metadata?.moveHistory || [];
  const fairnessPenalty = Math.pow(2, moveHistory.length) - 1;
  score += fairnessPenalty * fairnessWeight;

  // 4. Dwell length (longer = harder to re-place)
  const dwellBuckets = aircraft.departureBucket - aircraft.arrivalBucket;
  score += dwellBuckets * dwellWeight;

  // 5. Alternative spots (fewer = harder to move)
  // Use inverse ratio: 0 alternatives = max penalty, all spots = 0 penalty
  const alternativeCount = countAlternativeSpots(aircraft, spots);
  const totalSpots = spots.length;
  const flexibilityRatio = totalSpots > 0 ? alternativeCount / totalSpots : 0;
  const alternativesPenalty = (1 - flexibilityRatio) * alternativesWeight * 10;
  score += alternativesPenalty;

  return score;
}

/**
 * Count how many spots can accommodate this aircraft
 *
 * Used to determine flexibility for re-placement.
 * Aircraft with more alternatives are easier to move.
 *
 * @param aircraft Aircraft to evaluate
 * @param spots All available spots
 * @param checkTimeAvailability If true, only counts spots that are currently available (default: false)
 * @returns Number of spots that can accommodate this aircraft
 */
export function countAlternativeSpots(
  aircraft: ParkingRequest,
  spots: Spot[],
  checkTimeAvailability: boolean = false
): number {
  let count = 0;

  for (const spot of spots) {
    // Check if aircraft size is in spot's allowedSizes array
    if (!spot.allowedSizes.includes(aircraft.sizeCategory)) {
      continue;
    }

    // Optionally check time availability (more accurate but slower)
    if (checkTimeAvailability) {
      // Check if spot is available during aircraft's dwell
      let available = true;
      for (
        let bucket = aircraft.arrivalBucket;
        bucket < aircraft.departureBucket;
        bucket++
      ) {
        const wordIndex = Math.floor(bucket / 64);
        const bitIndex = BigInt(bucket % 64);
        if ((spot.occupancy[wordIndex] & (1n << bitIndex)) !== 0n) {
          available = false;
          break;
        }
      }
      if (!available) {
        continue;
      }
    }

    count++;
  }

  return count;
}
